/**
 * PDF Template Client using tRPC
 *
 * This client connects to the backend API via tRPC to manage PDF templates.
 * Uses the kernel-plugin-pdf router endpoints.
 */

import type { BlockTemplate as EditorBlockTemplate } from '@xhubio-saas/pdf-editor-frontend'
import type { Template } from '@xhubio-saas/pdf-frontend/plugin-pdf'
import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrgMeasurementSystem } from '@/lib/useOrgLocale'
import { trpc } from '@/trpc'

/**
 * Vorbelegung einer NEUEN Vorlage aus dem Maßsystem des Betriebs
 * (Plan 16, Schritt 3).
 *
 * | Org-Maßsystem | `lengthUnit` | `page.size` | Ränder    |
 * |---------------|--------------|-------------|-----------|
 * | `metric`      | `mm`         | `A4`        | 20 mm     |
 * | `imperial`    | `inch`       | `LETTER`    | 0,75 Zoll |
 *
 * 🔴 Die Organisation ist **Vorbelegung, nicht Auflösung**. Beide Werte stehen
 * danach an der Vorlage und reisen mit ihr: stellt der Betrieb später auf
 * imperial um, bleibt eine bestehende A4-Vorlage A4. Umgekehrt darf der
 * deutsche Betrieb, der einen Report für einen Amerikaner baut, genau diese
 * eine Vorlage auf LETTER stellen, ohne die Organisation anzufassen. Hinge die
 * Deutung am Betrachter, ergäbe dieselbe Datei in zwei Betrieben zwei
 * verschiedene PDFs.
 *
 * 🔴 Warum ein **vollständiges** v2-Shape und nicht bloß die zwei Felder: die
 * Editor-Seite erkennt eine Vorlage an `version` + `body`. Ein Objekt mit nur
 * `{ lengthUnit, page }` ist weder v2-Shape noch leer und fällt in den
 * Legacy-Zweig — der Sackgassen-Screen stünde direkt nach dem Anlegen.
 *
 * 🔴 Warum die Ränder mit der Einheit wechseln: `convertTemplateSpatialToPt`
 * rechnet `page.margins` mit `lengthUnit` um. Eine `20` wäre bei `inch`
 * 20 Zoll = 1440 pt und sprengt jede Seite. 20 mm (56,7 pt) und 0,75 Zoll
 * (54 pt) liegen dagegen beieinander. Im metrischen Fall ist das Ergebnis
 * deckungsgleich mit dem bisherigen Verhalten — eine Vorlage ohne `lengthUnit`
 * wurde beim Laden ohnehin als mm aufgelöst.
 */
export function starterTemplate(
  name: string,
  measurementSystem: 'metric' | 'imperial'
): EditorBlockTemplate {
  const imperial = measurementSystem === 'imperial'
  return {
    version: '1.0.0',
    name,
    page: {
      size: imperial ? 'LETTER' : 'A4',
      orientation: 'portrait',
      margins: imperial
        ? { top: 0.75, right: 0.75, bottom: 0.75, left: 0.75 }
        : { top: 20, right: 20, bottom: 20, left: 20 },
    },
    body: { blocks: [] },
    editorVersion: 'v2',
    lengthUnit: imperial ? 'inch' : 'mm',
  }
}

/**
 * Create template input
 */
export interface CreateTemplateInput {
  name: string
  description?: string
  type: string
}

/**
 * Eine Zeile der Vorlagen-Liste, so wie `loadProjectList` sie liefert.
 *
 * 🔴 Jedes Feld hier ist eines, das `TemplateListPage` (pdf-frontend)
 * anzeigt oder auswertet. Was hier fehlt, ist in der Liste unsichtbar,
 * obwohl `pdf.templateList` es liefert — genau so waren `description`
 * (Spalte stand auf „-", bei jeder Zeile) und `isSystem` (System-Badge
 * fehlte, „Bearbeiten"/„Loeschen" standen an System-Vorlagen und liefen
 * ins „Template not found") monatelang tot. QA-Befund 2026-09-07.
 */
export interface PdfTemplateListEntry {
  id: string
  name: string
  updatedAt: string
  /** Beschreibung aus der Tabellenspalte — Listenspalte „Description". */
  description?: string
  /**
   * System-Vorlage (aus `config/pdf-templates/`, nicht loeschbar).
   * `TemplateListPage` blendet daran „Bearbeiten"/„Loeschen" aus und
   * zeigt ein „System"-Abzeichen; „Duplizieren" bleibt der Ausweg.
   */
  isSystem?: boolean
  // Surfaced by pdf-backend >=2.37 as a synthetic column on
  // templateList rows — drives the v1/v2 badge + the legacy
  // editor's row-disable behaviour.
  editorVersion?: 'v1' | 'v2'
  // Free-form document type so the V2 list's create dialog can
  // suggest types the org already uses ("Rechnung", "Angebot",
  // …). Pre-existing column on the templates table.
  documentType?: string
}

/**
 * Eine Listenzeile in die Form bringen, die `TemplateListPage` liest.
 *
 * Eigene Funktion, damit die Abbildung pruefbar ist: der Verlust von
 * `description`/`isSystem` sass vorher in einem Objektliteral mitten in
 * einem `useCallback` und war ohne laufenden Browser nicht zu sehen.
 */
export function toListTemplate(p: PdfTemplateListEntry): Template {
  return {
    id: p.id,
    name: p.name,
    description: p.description,
    type: p.documentType,
    isSystem: p.isSystem,
    editorVersion: p.editorVersion,
    // Die Liste zeigt nur „Zuletzt geaendert"; ein eigenes Anlagedatum
    // liefert `templateList` nicht, deshalb steht hier derselbe Wert.
    createdAt: p.updatedAt,
    updatedAt: p.updatedAt,
  }
}

/**
 * PDF Template Client interface
 */
export interface PdfTemplateClient {
  loadProjectList: () => Promise<
    { success: true; projects: PdfTemplateListEntry[] } | { success: false; error: string }
  >

  deleteTemplate: (id: string) => Promise<{ success: true } | { success: false; error: string }>

  duplicateTemplate: (
    id: string
  ) => Promise<{ success: true; newId: string } | { success: false; error: string }>

  createTemplate: (
    input: CreateTemplateInput
  ) => Promise<{ success: true; id: string } | { success: false; error: string }>
}

/**
 * Creates a PDF Template client that uses tRPC to communicate with the backend.
 *
 * This function should be called within a React component that has access
 * to the tRPC context (inside TRPCProvider).
 *
 * @returns PdfTemplateClient implementation using tRPC
 */
export function usePdfTemplateClient(): PdfTemplateClient {
  const utils = trpc.useUtils()

  // Get mutation functions - using correct procedure names from kernel-plugin-pdf
  const createMutation = trpc.pdf.templateCreate.useMutation()
  const deleteMutation = trpc.pdf.templateDelete.useMutation()
  const duplicateMutation = trpc.pdf.templateDuplicate.useMutation()

  // Die Mutationen wandern in eine Ref: ihr Inhalt ist pro Render neu, die
  // Ref-Identitaet bleibt stabil. Gelesen wird `.current` NUR im asynchronen
  // Callback, nie waehrend des Renderns.
  const mutations = useRef({
    createMutation,
    deleteMutation,
    duplicateMutation,
  })
  mutations.current = {
    createMutation,
    deleteMutation,
    duplicateMutation,
  }

  // Das Massystem des Betriebs wandert aus demselben Grund wie die Mutationen
  // in eine Ref: es trifft erst ein, wenn die Org-Query aufgeloest ist, und
  // duerfte sonst die Client-Identitaet wechseln lassen. Gelesen wird es nur
  // im Callback, nie waehrend des Renderns.
  const measurementSystem = useOrgMeasurementSystem()
  const measurementSystemRef = useRef(measurementSystem)
  measurementSystemRef.current = measurementSystem

  // Das Suffix des Duplikats kommt aus dem Katalog des `pdf`-Namensraums
  // (`templates.copySuffix`, zwoelf Sprachen) — es wandert aus demselben Grund
  // wie das Massystem in eine Ref: `t` wechselt beim Sprachwechsel die
  // Identitaet und duerfte sonst die Client-Identitaet mitreissen. Gelesen wird
  // es nur im Callback, nie waehrend des Renderns.
  // 🔴 Kein englischer Ersatztext im Code: der Schluessel steht in ALLEN zwoelf
  // Katalogen (Waechter `pdf-frontend/tests/locales-fuehren-jeden-schluessel.test.ts`),
  // und die i18next-Kette faellt auf die Fallback-Sprache zurueck, nicht auf ein
  // Literal. Ein englisches Ersatz-Suffix hier waere genau das Wort, das dieser
  // Plan aus der deutschen Oberflaeche nimmt — und das Fertig-Kriterium des Plans
  // misst, dass es in dieser Datei nicht mehr steht.
  const { t } = useTranslation('pdf')
  const copySuffixRef = useRef(t('templates.copySuffix'))
  copySuffixRef.current = t('templates.copySuffix')

  return useMemo(
    () => ({
      async loadProjectList(): Promise<
        { success: true; projects: PdfTemplateListEntry[] } | { success: false; error: string }
      > {
        try {
          // Invalidate cache to ensure fresh data
          await utils.pdf.templateList.invalidate()
          const result = await utils.pdf.templateList.fetch({
            limit: 100,
            offset: 0,
            includeInactive: false,
          })

          const projects = (result.templates || []).map(
            (t: {
              id: string
              name: string
              updatedAt: string | Date
              description?: string | null
              isSystem?: boolean | null
              documentType?: string | null
              editorVersion?: 'v1' | 'v2' | null
            }) => ({
              id: t.id,
              name: t.name,
              updatedAt: typeof t.updatedAt === 'string' ? t.updatedAt : t.updatedAt.toISOString(),
              description: t.description ?? undefined,
              isSystem: t.isSystem ?? undefined,
              documentType: t.documentType ?? undefined,
              editorVersion: t.editorVersion ?? undefined,
            })
          )

          return { success: true, projects }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to load project list'
          // Detect plan upgrade required - not a system error, just a plan limitation
          const isPlanUpgrade = message.includes('Plan upgrade required')
          return {
            success: false,
            error: isPlanUpgrade ? 'PLAN_UPGRADE_REQUIRED' : message,
          }
        }
      },

      async deleteTemplate(
        id: string
      ): Promise<{ success: true } | { success: false; error: string }> {
        try {
          await mutations.current.deleteMutation.mutateAsync({ id, permanent: false })
          // Invalidate cache so list will refetch
          await utils.pdf.templateList.invalidate()
          return { success: true }
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Failed to delete template',
          }
        }
      },

      async duplicateTemplate(
        id: string
      ): Promise<{ success: true; newId: string } | { success: false; error: string }> {
        try {
          // First load the template to duplicate
          const template = await utils.pdf.templateGet.fetch({ id })

          if (!template) {
            return { success: false, error: 'Template not found' }
          }

          // Kopiert wird auf dem SERVER: `templateDuplicate` nimmt die Id und
          // den neuen Namen. Vorher baute der Browser die Kopie selbst aus dem
          // geladenen Datensatz zusammen (name, documentType, template,
          // pageSettings, sampleData, isDefault) — ein Rundweg, der zwei Schaeden
          // hatte:
          //
          //   🔴 `description` und `countryCode` standen nicht in der Liste und
          //      gingen bei jeder Kopie still verloren.
          //   🔴 `template`/`pageSettings`/`sampleData` sind jsonb; seit
          //      pdf-backend 2.60.x liefert `templateGet` sie als `unknown`
          //      statt als `Record<string, unknown>` — der Rundweg war damit
          //      nicht mehr typisierbar, ohne ihn per Zusicherung zuzukleben.
          //
          // Beides faellt weg, sobald der Server kopiert. Dieselbe Prozedur
          // faehrt saas-dashandwerk-frontend seit jeher.
          const result = await mutations.current.duplicateMutation.mutateAsync({
            id,
            newName: `${template.name}${copySuffixRef.current}`,
          })

          // Invalidate cache so list will refetch
          await utils.pdf.templateList.invalidate()
          return { success: true, newId: result.id }
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Failed to duplicate template',
          }
        }
      },

      async createTemplate(
        input: CreateTemplateInput
      ): Promise<{ success: true; id: string } | { success: false; error: string }> {
        try {
          // Leere, aber vollstaendige Vorlage — Einheit und Papierformat aus
          // dem Massystem des Betriebs vorbelegt (siehe `starterTemplate`).
          // Dazu der JSONB-Spiegel `templateType`: das Typ-Feld des Editors
          // (Freitext-Combobox seit pdf-editor-frontend 1.94) liest ihn, nicht
          // die Spalte — ohne ihn faellt es fuer jede Art ausserhalb seines
          // Katalogs auf „Sonstiges" zurueck.
          const template = {
            ...starterTemplate(input.name, measurementSystemRef.current),
            templateType: input.type,
          }
          const result = await mutations.current.createMutation.mutateAsync({
            name: input.name,
            description: input.description,
            // Die Dokumentart ist Nutzereingabe: fehlt sie, setzt pdf-backend
            // seine Schema-Vorgabe ein (Issue PDF-API 09-06).
            documentType: input.type,
            template: template as unknown as Record<string, unknown>,
            pageSettings: template.page as unknown as Record<string, unknown>,
            sampleData: {}, // Empty object - backend expects record type
            isDefault: false,
          })
          // Invalidate cache so list will refetch
          await utils.pdf.templateList.invalidate()
          return { success: true, id: result.id }
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Failed to create template',
          }
        }
      },
    }),
    // NUR `utils`. Keine Mutationen — sonst ist der Client pro Render neu.
    [utils]
  )
}
